import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Package, Pencil } from 'lucide-react';

const Items = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    hsn: '',
    unit: 'PCS',
    salePrice: 0,
    purchasePrice: 0,
    gstPercent: 18,
    stock: 0,
    lowStockAlert: 10
  });
  const businessId = localStorage.getItem('currentBusiness');

  useEffect(() => {
    fetchItems();
  }, [search]);

  const fetchItems = async () => {
    try {
      const response = await api.get(`/api/items/${businessId}`, {
        params: { search }
      });
      setItems(response.data.items);
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/api/items/${businessId}/${editingItem.id}`, formData);
        toast.success('Item updated successfully');
      } else {
        await api.post('/api/items', { ...formData, businessId });
        toast.success('Item created successfully');
      }
      setShowDialog(false);
      setEditingItem(null);
      setFormData({ name: '', hsn: '', unit: 'PCS', salePrice: 0, purchasePrice: 0, gstPercent: 18, stock: 0, lowStockAlert: 10 });
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      hsn: item.hsn || '',
      unit: item.unit,
      salePrice: item.salePrice,
      purchasePrice: item.purchasePrice || 0,
      gstPercent: item.gstPercent,
      stock: item.stock,
      lowStockAlert: item.lowStockAlert || 10
    });
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingItem(null);
    setFormData({ name: '', hsn: '', unit: 'PCS', salePrice: 0, purchasePrice: 0, gstPercent: 18, stock: 0, lowStockAlert: 10 });
  };

  return (
    <div className="space-y-6" data-testid="items-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading">Items & Inventory</h1>
          <p className="text-sm text-gray-600 mt-1">Manage your product catalog</p>
        </div>
        <Button className="bg-teal-700 hover:bg-teal-800" onClick={() => setShowDialog(true)} data-testid="add-item-button">
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
        <Dialog open={showDialog} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="item-form">
              <div className="space-y-2">
                <Label htmlFor="name">Item Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="item-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hsn">HSN Code</Label>
                  <Input
                    id="hsn"
                    value={formData.hsn}
                    onChange={(e) => setFormData({ ...formData, hsn: e.target.value })}
                    data-testid="item-hsn-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Input
                    id="unit"
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    data-testid="item-unit-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="salePrice">Sale Price <span className="text-red-500">*</span></Label>
                  <Input
                    id="salePrice"
                    type="number"
                    step="0.01"
                    value={formData.salePrice}
                    onChange={(e) => setFormData({ ...formData, salePrice: parseFloat(e.target.value) })}
                    required
                    data-testid="item-sale-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="purchasePrice">Purchase Price</Label>
                  <Input
                    id="purchasePrice"
                    type="number"
                    step="0.01"
                    value={formData.purchasePrice}
                    onChange={(e) => setFormData({ ...formData, purchasePrice: parseFloat(e.target.value) })}
                    data-testid="item-purchase-price-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gstPercent">GST %</Label>
                  <Input
                    id="gstPercent"
                    type="number"
                    step="0.01"
                    value={formData.gstPercent}
                    onChange={(e) => setFormData({ ...formData, gstPercent: parseFloat(e.target.value) })}
                    data-testid="item-gst-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stock">Opening Stock</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: parseFloat(e.target.value) })}
                    data-testid="item-stock-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lowStockAlert">Low Stock Alert</Label>
                <Input
                  id="lowStockAlert"
                  type="number"
                  value={formData.lowStockAlert}
                  onChange={(e) => setFormData({ ...formData, lowStockAlert: parseFloat(e.target.value) })}
                  data-testid="item-low-stock-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="item-submit-button">
                {editingItem ? 'Update Item' : 'Create Item'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="item-search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <Card key={item.id} className="hover:shadow-md transition-shadow" data-testid="item-card">
              <CardContent className="pt-6">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{item.name}</h3>
                    {item.stock <= (item.lowStockAlert || 0) && (
                      <Badge variant="destructive" className="text-xs mt-1">Low Stock</Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(item)}
                    data-testid="edit-item-button"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sale Price:</span>
                    <span className="font-semibold text-teal-700">₹{item.salePrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Stock:</span>
                    <span className="font-semibold">{item.stock} {item.unit}</span>
                  </div>
                  {item.hsn && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">HSN:</span>
                      <span className="font-mono text-xs">{item.hsn}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">GST:</span>
                    <span>{item.gstPercent}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>No items found. Add your first item to get started!</p>
        </div>
      )}
    </div>
  );
};

export default Items;